import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  X,
  ArrowRight,
  Save,
  ChevronLeft,
  UserCircle,
  Workflow as WorkflowIcon
} from 'lucide-react';
import { createTemplate } from '../../../services/workflowTemplateService.ts';
import { fetchApprovers } from '../../../services/approverService.ts';
import { useNotify } from 'react-admin';

interface ApprovalStep {
  id: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
}

const TemplateCreate: React.FC = () => {
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem('userId'));
  const createdByValue = Number.isFinite(currentUserId) ? currentUserId : 0;
  //define the menu navigation
  const templateUrl = '/QmsWorkflowTemplate';
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(true);

  const [template, setTemplate] = useState<ApprovalTemplate>({
    Name: '',
    docType: 'SOP',
    description: '',
    steps: [],
    isActive: true,
    createdDate: new Date().toISOString().split('T')[0],
    createdBy: createdByValue,
    lastModified: new Date().toISOString().split('T')[0],
    usageCount: 0
  });

  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  useEffect(() => {
    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data);
      } catch (error) {
        console.error('Failed to load users:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [notify]);

  const handleAddStep = () => {
    const newStep: ApprovalStep = {
      id: `s${template.steps.length + 1}`,
      stepNo: template.steps.length + 1,
      Name: `Step ${template.steps.length + 1}`,
      stepType: 'single',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setTemplate({
      ...template,
      steps: [...template.steps, newStep]
    });
  };

  const handleDeleteStep = (stepId: string) => {
    const updatedSteps = template.steps
      .filter(s => s.id !== stepId)
      .map((s, idx) => ({ ...s, stepNo: idx + 1 }));
    setTemplate({
      ...template,
      steps: updatedSteps
    });
  };

  const updateStepField = (stepId: string, field: keyof ApprovalStep, value: any) => {
    setTemplate({
      ...template,
      steps: template.steps.map(s =>
        s.id === stepId ? { ...s, [field]: value } : s
      )
    });
  };

  const handleAddApprover = (stepId: string, approver: Approver) => {
    setTemplate({
      ...template,
      steps: template.steps.map(s =>
        s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
      )
    });
  };

  const handleRemoveApprover = (stepId: string, approverId: string) => {
    setTemplate({
      ...template,
      steps: template.steps.map(s =>
        s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
      )
    });
  };

  const handleSubmit = async () => {
    if (!template.Name.trim()) {
      notify('Please enter the template name', { type: 'error' });
      return;
    }

    try {
      setIsSubmitting(true);
      await createTemplate(template);
      notify('Template created successfully', { type: 'success' });
      navigate(`${templateUrl}/list`);
    } catch (error) {
      console.error('Failed to create template:', error);
      notify('Failed to create template', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`${templateUrl}/list`)}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <ChevronLeft className="size-4" />
          Back to Templates
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create New Approval Workflow Template</h1>
        <p className="text-gray-600 mt-2">Define the approval steps and approvers for your workflow</p>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {/* Basic Information */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                  type="text"
                  value={template.Name}
                  onChange={(e) => setTemplate({ ...template, Name: e.target.value })}
                  placeholder="e.g., SOP Standard Approval Flow"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={template.docType}
                onChange={(e) => setTemplate({ ...template, docType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
              >
                <option value="SOP">SOP</option>
                {/* <option value="WI">Work Instruction</option>
                <option value="FORM">Form</option>
                <option value="POLICY">Policy</option> */}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={template.description}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                placeholder="Describe the purpose and scope of this workflow template..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Approval Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Approval Steps</h2>
            <button
              onClick={handleAddStep}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus className="size-4" />
              Add Step
            </button>
          </div>

          <div className="space-y-4">
            {template.steps.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                <WorkflowIcon className="size-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No approval steps yet</p>
                <button
                  onClick={handleAddStep}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="size-5" />
                  Add First Step
                </button>
              </div>
            ) : (
              template.steps.map((step, index) => (
                <div key={step.id}>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                          {step.stepNo}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Step Name
                            </label>
                            <input
                              type="text"
                              value={step.Name}
                              onChange={(e) => updateStepField(step.id, 'Name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Step Type
                            </label>
                            <select
                              value={step.stepType}
                              onChange={(e) => updateStepField(step.id, 'stepType', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm"
                            >
                              <option value="single">Single Approver</option>
                              <option value="parallel">Parallel Approval</option>
                              <option value="conditional">Conditional Approval</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea
                              value={step.description}
                              onChange={(e) => updateStepField(step.id, 'description', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Approval Rule
                            </label>
                            <select
                              value={step.approvalRule}
                              onChange={(e) => updateStepField(step.id, 'approvalRule', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm"
                            >
                              <option value="all">All Approvers</option>
                              <option value="any">Any Approver</option>
                              {/* <option value="majority">Majority</option> */}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Time Limit (days)
                            </label>
                            <input
                              type="number"
                              value={step.timeLimit || ''}
                              onChange={(e) => updateStepField(step.id, 'timeLimit', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Approvers
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {step.approvers.map(approver => (
                              <div
                                key={approver.id}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                              >
                                <UserCircle className="size-4 text-gray-600" />
                                <div>
                                  <div className="font-medium text-gray-900">{approver.name}</div>
                                  <div className="text-xs text-gray-500">{approver.role}</div>
                                </div>
                                <button
                                  onClick={() => handleRemoveApprover(step.id, approver.id)}
                                  className="ml-2 text-gray-400 hover:text-red-600"
                                >
                                  <X className="size-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                setCurrentStepId(step.id);
                                setShowApproverModal(true);
                              }}
                              className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                              <Plus className="size-4" />
                              Add Approver
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={step.isOptional}
                              onChange={(e) => updateStepField(step.id, 'isOptional', e.target.checked)}
                              className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">This step is optional</span>
                          </label>
                          <button
                            onClick={() => handleDeleteStep(step.id)}
                            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                          >
                            <Trash2 className="size-4" />
                            Delete Step
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {index < template.steps.length - 1 && (
                    <div className="flex justify-center py-3">
                      <ArrowRight className="size-6 text-gray-400 transform rotate-90" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={() => navigate(`${templateUrl}/list`)}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            <Save className="size-5" />
            {isSubmitting ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={() => {
                    setShowApproverModal(false);
                    setCurrentStepId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {isLoadingApprovers ? (
                <div className="text-center text-gray-600">Loading users...</div>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                  {approversPool.map(approver => {
                    const currentStep = template.steps.find(s => s.id === currentStepId);
                    const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id);

                    return (
                      <button
                        key={approver.id}
                        onClick={() => {
                          if (!isAlreadyAdded) {
                            handleAddApprover(currentStepId, approver);
                          }
                        }}
                        disabled={isAlreadyAdded}
                        className={`p-4 border rounded-lg text-left transition-colors ${
                          isAlreadyAdded
                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                            : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <UserCircle className="size-10 text-gray-400" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{approver.name}</div>
                            <div className="text-sm text-gray-600">{approver.role}</div>
                            <div className="text-xs text-gray-500">{approver.email}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowApproverModal(false);
                  setCurrentStepId(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateCreate;
